import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';
import posthog from '../lib/posthog';

/**
 * AdSlot — fetches one ad for a placement, logs a single impression on mount,
 * and logs a click on CTA. Renders nothing if no ad is available.
 *
 * Layout variants:
 *   - 'card'   (default) — full-width feed-style card
 *   - 'compact'           — sidebar-style narrow card
 */
const AdSlot = ({ placement, variant = 'card' }) => {
  const { profile } = useAuth();
  const [ad, setAd] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const impressionLoggedRef = useRef(false);

  // Fetch one ad
  useEffect(() => {
    let cancelled = false;
    api.serveAd(placement, profile?.id)
      .then(res => {
        if (!cancelled) {
          setAd(res.data?.ad || null);
          setLoaded(true);
        }
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [placement, profile?.id]);

  // Fire ad_placeholder_viewed once when no ad is available (loaded but slot is empty)
  useEffect(() => {
    if (loaded && !ad) posthog.capture('ad_placeholder_viewed', { placement });
  }, [loaded, ad, placement]);

  // Impression beacon (once per mount per ad)
  useEffect(() => {
    if (!ad || impressionLoggedRef.current) return;
    impressionLoggedRef.current = true;
    api.logAdImpression(ad.id, { placement, user_id: profile?.id }).catch(() => {});
  }, [ad, placement, profile?.id]);

  if (!loaded || !ad) return null;

  const handleClick = (e) => {
    api.logAdClick(ad.id, { placement, user_id: profile?.id }).catch(() => {});
    // Let the anchor's default open-in-new-tab behaviour proceed.
  };

  if (variant === 'compact') {
    return (
      <div className="rounded-arch border border-primary/15 bg-white/60 dark:bg-white/10 backdrop-blur p-4 space-y-2"
        data-testid={`ad-slot-${placement}`}>
        <span className="text-[10px] uppercase tracking-wider text-text-muted/70">Sponsored</span>
        {ad.image_url && (
          <img src={ad.image_url} alt={ad.headline} className="w-full h-24 object-cover rounded-md" loading="lazy" />
        )}
        <p className="text-sm font-medium text-text-main leading-snug">{ad.headline}</p>
        {ad.body && <p className="text-xs text-text-muted leading-relaxed line-clamp-3">{ad.body}</p>}
        <a href={ad.cta_url} target="_blank" rel="noopener noreferrer sponsored"
          onClick={handleClick}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          data-testid={`ad-cta-${placement}`}>
          {ad.cta_label} <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    );
  }

  return (
    <div className="rounded-arch-xl border border-primary/15 bg-white/60 dark:bg-white/10 backdrop-blur p-5 flex gap-4 items-start"
      data-testid={`ad-slot-${placement}`}>
      {ad.image_url && (
        <img src={ad.image_url} alt={ad.headline} className="w-24 h-24 object-cover rounded-arch flex-shrink-0" loading="lazy" />
      )}
      <div className="flex-1 min-w-0">
        <span className="text-[10px] uppercase tracking-wider text-text-muted/70">Sponsored</span>
        <p className="text-base font-medium text-text-main leading-snug mt-1">{ad.headline}</p>
        {ad.body && <p className="text-sm text-text-muted leading-relaxed mt-1 line-clamp-2">{ad.body}</p>}
        <a href={ad.cta_url} target="_blank" rel="noopener noreferrer sponsored"
          onClick={handleClick}
          className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full text-xs font-medium bg-primary text-white hover:bg-primary/90"
          data-testid={`ad-cta-${placement}`}>
          {ad.cta_label} <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
};

export default AdSlot;
